import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Detail, Input, Tooltip, Icon, Optional } from 'components/common';
import { useGet } from 'hooks';
import { userKeywords } from 'services/api';

const Token = styled.div`
  display: flex;
  background: ${p => p.theme.includeFilter};
  border: 1px solid ${p => p.theme.teal};
  font-weight: 500;
  color: ${p => p.theme.black};
  text-transform: uppercase;
  font-size: 10px;
  padding: 0 5px;
  border-radius: 3px;
  margin: 0 2px 0px 0;
  align-items: center;
  word-break: break-word;
  cursor: default;
  > div {
    padding: 3px;
    cursor: pointer;
  }
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  border: ${p =>
    p.isFocused ? '1px solid #40a9ff' : '1px solid rgba(0, 0, 0, 0.15)'};
  border-radius: 2px;
  padding: 5px;
  position: relative;
  font-size: 14px;
  transition: all 0.3s ease;
  background: ${p => (p.disabled ? '#f5f5f5' : 'white')};
  pointer-events: ${p => (p.disabled ? 'none' : 'all')};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'text')};
  box-shadow: ${p =>
    p.isFocused
      ? '0 0 0 2px rgba(24, 144, 255, 0.2)'
      : '0 0 0 0px rgba(24, 144, 255, 0.2)'};
  min-height: 32px;

  input {
    border: none;
    padding: 0 6px;
    height: auto;
    flex: 1;
    min-width: 50px;
    background: ${p => (p.disabled ? '#f5f5f5' : 'white')};

    :focus {
      outline: none;
      border: 0;
      box-shadow: none;
    }
  }

  :hover {
    border: ${p =>
      p.disabled ? '1px solid rgba(0, 0, 0, 0.15)' : '1px solid #40a9ff'};
  }
`;

const OptionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 5px);
  left: 0;
  right: 0;
  z-index: 10;
`;

const OptionsContent = styled.div`
  background: white;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
  max-height: 200px;
  overflow: auto;
`;

const Option = styled.div`
  padding: 4px 16px;
  cursor: ${p => (p.isAlreadySelected ? 'default' : 'pointer')};
  background: ${p => {
    if (p.isAlreadySelected) return 'rgba(173, 216, 230, 0.3)';
    if (p.selected) return 'rgba(24, 144, 255, 0.1)';
    return 'transparent';
  }};
  display: flex;
  justify-content: space-between;
  align-items: center;

  :hover {
    background: ${p => {
      if (p.isAlreadySelected) return 'rgba(173, 216, 230, 0.3)';
      return 'rgba(0, 0, 0, 0.05)';
    }};
  }

  :first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  :last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

const InputWrapper = styled.div`
  position: relative;
`;

function Keywords({ value = [], onChange, style, loading, disabled }) {
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [showOptions, setShowOptions] = useState(false);
  const [isMouseInDropdown, setIsMouseInDropdown] = useState(false);
  const inputRef = useRef(null);
  const optionsRef = useRef(null);

  // Fetch account keywords
  const [{ res: accountKeywords, error: keywordsError }] = useGet({
    url: userKeywords,
    onMount: true,
  });

  // Filter account keywords based on input
  const filteredOptions = useMemo(() => {
    // If there's an API error or no keywords loaded, return empty array (graceful degradation)
    if (keywordsError || !accountKeywords) return [];

    const searchTerm = inputValue.toLowerCase().trim();

    if (!searchTerm) {
      // Show first 10 account keywords when no input
      return accountKeywords.slice(0, 10).map(item => ({
        keyword: item.keyword,
        isSelected: value.some(
          token => token.toLowerCase() === item.keyword.toLowerCase()
        ),
      }));
    }

    // Existing filtering logic for when user types
    return accountKeywords
      .filter(item => item.keyword.toLowerCase().includes(searchTerm))
      .slice(0, 10) // Limit to 10 suggestions as per PRD
      .map(item => ({
        keyword: item.keyword,
        isSelected: value.some(
          token => token.toLowerCase() === item.keyword.toLowerCase()
        ),
      }));
  }, [accountKeywords, inputValue, value, keywordsError]);

  // Scroll selected option into view
  useEffect(() => {
    if (selectedIndex >= 0 && optionsRef.current) {
      const selectedOption = optionsRef.current.children[selectedIndex];
      if (selectedOption) {
        selectedOption.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedIndex]);

  const createToken = tokenValue => {
    const toAdd = tokenValue || inputValue;
    if (!toAdd.trim()) return;

    // Check for duplicates (silent ignore as per PRD)
    if (
      value.some(token => token.toLowerCase() === toAdd.toLowerCase().trim())
    ) {
      if (!tokenValue) {
        // Only clear input and hide dropdown if manually typed
        setInputValue('');
        setShowOptions(false);
        setSelectedIndex(-1);
      }
      return;
    }

    // Check token limit
    if (value.length >= 10) return;

    // Check character limit
    const totalLength = value.reduce(
      (prev, current) => prev + current.length,
      0
    );
    if (totalLength + toAdd.length > 255) return;
    onChange([...value, toAdd.trim()]);

    // Always clear input and hide dropdown after adding a token
    setInputValue('');
    setShowOptions(false);
    setSelectedIndex(-1);
  };

  const removeToken = (index, e) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
    e && e.stopPropagation();
  };

  const handleKeyDown = e => {
    if (showOptions && filteredOptions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredOptions.length - 1 ? prev + 1 : 0
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev > 0 ? prev - 1 : filteredOptions.length - 1
        );
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (selectedIndex >= 0) {
          const selectedOption = filteredOptions[selectedIndex];
          if (selectedOption.isSelected) {
            // Remove the keyword if it's already selected
            const indexToRemove = value.findIndex(
              token =>
                token.toLowerCase() === selectedOption.keyword.toLowerCase()
            );
            if (indexToRemove !== -1) {
              removeToken(indexToRemove);
            }
          } else {
            createToken(selectedOption.keyword);
          }
        } else {
          createToken();
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setShowOptions(false);
        setSelectedIndex(-1);
      }
    } else {
      if (e.key === 'Enter' || e.key === 'Tab') {
        createToken();
        e.preventDefault();
      } else if (inputValue === '' && e.key === 'Backspace') {
        removeToken(value.length - 1);
      }
    }
  };

  const handleBlur = () => {
    // Only close if mouse is not in dropdown
    if (!isMouseInDropdown) {
      // Delay to allow option clicks to register
      setTimeout(() => {
        setIsFocused(false);
        setShowOptions(false);
        setSelectedIndex(-1);
        setInputValue('');
      }, 200);
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
    // Don't show options if maximum keywords reached
    if (value.length >= 10) {
      return;
    }
    if (filteredOptions.length > 0) {
      setShowOptions(true);
    }
  };

  const handleInputChange = newValue => {
    // Prevent input changes if maximum keywords reached
    if (value.length >= 10) {
      return;
    }

    setInputValue(newValue);
    setSelectedIndex(-1);

    if (newValue.trim()) {
      setShowOptions(true);
    } else {
      setShowOptions(false);
    }
  };

  const handleOptionClick = option => {
    if (option.isSelected) {
      // Remove the keyword if it's already selected
      const indexToRemove = value.findIndex(
        token => token.toLowerCase() === option.keyword.toLowerCase()
      );
      if (indexToRemove !== -1) {
        removeToken(indexToRemove);
      }
      inputRef.current?.focus();
      return;
    }

    createToken(option.keyword);
    // Prevent blur from closing dropdown and refocus
    inputRef.current?.focus();
  };

  const handleInputContainerClick = () => {
    inputRef.current?.focus();
    // If input is already focused and dropdown is closed, reopen it
    if (
      isFocused &&
      !showOptions &&
      value.length < 10 &&
      filteredOptions.length > 0
    ) {
      setShowOptions(true);
    }
  };

  return (
    <div style={style}>
      <Detail
        label={
          <>
            Keywords
            <Optional />
          </>
        }
      >
        <Tooltip
          title={
            disabled
              ? 'Option disabled because the contact has no activities.'
              : ''
          }
        >
          <div style={{ width: '100%' }}>
            {/* Input field with inline tokens and typeahead */}
            <InputWrapper>
              <InputContainer
                onClick={handleInputContainerClick}
                isFocused={isFocused}
                disabled={loading || disabled}
              >
                {/* Tokens displayed inline with input */}
                {value.map((token, i) => (
                  <Token key={`${token}-${i}`} data-testid={`token-${i}`}>
                    {token}
                    <div
                      data-testid='token-delete'
                      onClick={e => removeToken(i, e)}
                    >
                      <Icon
                        icon='delete'
                        size='sm'
                        color='rgba(0, 0, 0, 0.45)'
                      />
                    </div>
                  </Token>
                ))}
                <Input
                  data-testid='token-input'
                  onKeyDown={handleKeyDown}
                  onBlur={handleBlur}
                  onChange={handleInputChange}
                  onFocus={handleFocus}
                  value={inputValue}
                  placeholder={
                    value.length === 0
                      ? 'Type to search account keywords or enter your own'
                      : ''
                  }
                  ref={inputRef}
                  type='search'
                  disabled={loading || disabled}
                  readOnly={value.length >= 10}
                />
              </InputContainer>

              {/* Typeahead options dropdown */}
              {showOptions && filteredOptions.length > 0 && (
                <OptionsContainer
                  onMouseEnter={() => setIsMouseInDropdown(true)}
                  onMouseLeave={() => setIsMouseInDropdown(false)}
                >
                  <OptionsContent ref={optionsRef}>
                    {filteredOptions.map((option, index) => (
                      <Option
                        key={option.keyword}
                        selected={index === selectedIndex}
                        isAlreadySelected={option.isSelected}
                        onMouseDown={e => e.preventDefault()} // Prevent blur
                        onClick={() => handleOptionClick(option)}
                        onMouseEnter={() => setSelectedIndex(index)}
                      >
                        <span>{option.keyword}</span>
                        {option.isSelected && (
                          <Icon icon='check' size='sm' color='#40a9ff' />
                        )}
                      </Option>
                    ))}
                  </OptionsContent>
                </OptionsContainer>
              )}
            </InputWrapper>

            <div
              style={{
                textAlign: 'right',
                color: '#8D8D8D',
                fontSize: '13px',
                marginTop: '3px',
              }}
            >
              {value.length >= 10
                ? 'Maximum of 10 keywords reached'
                : `${value.length} / 10`}
            </div>
          </div>
        </Tooltip>
      </Detail>
    </div>
  );
}

Keywords.propTypes = {
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  style: PropTypes.shape({}),
};

export default Keywords;
